/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { ParentSize } from "@visx/responsive";
import {
  BrickLoader,
  UniversalWrap,
  getColorsPalette,
  getTableColumns,
  mapAccumulatedCostChartColors,
  StackBarChart,
  getEndDate,
  ChartType,
  CostAnalysisFields,
  Granularity,
  Period,
} from "~/components";
import { useCurrency } from "~/hooks";
import { ApplicationCostTableData } from "../ApplicationCostTableData";
import { useApplicationAccumulatedCostChart } from "../hooks";

type Props = {
  appId: string;
  startDate: string;
  endDate: string;
  granularity: Granularity;
  period: Period;
  hasFooter: boolean;
  showCostTable: boolean;
};

export function ApplicationAccumulatedCostView({
  appId,
  startDate,
  endDate,
  granularity,
  period,
  hasFooter,
  showCostTable,
}: Props) {
  const { currency } = useCurrency();

  const {
    accumulatedCostTableData,
    accumulatedCostTotal,
    accumulatedCostChartData,
    loading,
    error,
  } = useApplicationAccumulatedCostChart({
    appId,
    startDate,
    endDate: getEndDate(endDate),
    period,
    granularity,
  });

  const colors = mapAccumulatedCostChartColors([CostAnalysisFields.DAILY_COST]);

  const columns = useMemo(
    () =>
      getTableColumns({
        startDate,
        endDate,
        currency,
        data: accumulatedCostTableData,
        hasFooter,
        period,
        chartType: ChartType.ACCUMULATED_COST,
        colorsPalette: getColorsPalette(ChartType.ACCUMULATED_COST),
      }),
    [startDate, endDate, currency, hasFooter, accumulatedCostTableData]
  );

  return (
    <UniversalWrap
      loaderProps={{ loading, Component: BrickLoader }}
      errorProps={{ error }}
    >
      <ParentSize>
        {(parent) => (
          <StackBarChart
            width={parent.width}
            height={280}
            data={accumulatedCostChartData}
            granularity={granularity}
            loading={loading}
            startDate={startDate}
            endDate={endDate}
            period={period}
            keys={[CostAnalysisFields.DAILY_COST]}
            colors={colors}
            chartType={ChartType.ACCUMULATED_COST}
          />
        )}
      </ParentSize>
      <ApplicationCostTableData
        period={period}
        startDate={startDate}
        endDate={endDate}
        tableData={accumulatedCostTableData}
        chartType={ChartType.ACCUMULATED_COST}
        total={accumulatedCostTotal}
        tableError={error}
        columns={columns}
        isLoading={loading}
        showCostTable={showCostTable}
        hasFooter={hasFooter}
      />
    </UniversalWrap>
  );
}
